import { EntityTemplateField, EntityTemplateStep, IncidentLogStandardField, isIncidentLogStandardField } from "../../entityTemplates/EntityTemplate.types";
import { IncidentLogFieldModel } from "../../entityTemplates/IncidentLogFieldRenderers";
import { hasFieldData } from "../../entityTemplates/fieldValue";
import { ValidationError } from "@common/validationError";
import { isNullOrWhitespace } from "@common/stringOperations";
import { useTranslation } from "@localization/useTranslation";
import { exhaustiveGuard } from "@common/utilities";
import { useTagsMultiSelectAutoUpdater } from "@common/components/tagsMultiSelect/tagsMultiSelectAutoUpdater/TagsMultiSelectAutoUpdater.hooks";
import { TagRelationType } from "@common/components/tagsMultiSelect/TagsMultiSelect.types";
import { useRelationsQuery } from "@common/hooks/Relations.hooks";
import { useResponsibleQuery } from "../../user/hooks/useResponsibleQuery";
import { useIncidentDocuments } from "../incidentLogOverview/documents/IncidentDocument.hooks";
import { hasPersonalDataToCopy, isAccessRestricted } from "../../entityTemplates/fields/incidentLogFields/incidentLogAccessRequestOutcomeVisibility";

// Tags, relations and documents are saved through their own endpoints rather than on the incident, so their values come
// from the queries the fields themselves render from.
export interface IncidentLogRelatedValues {
    selectedTags: Array<string>;
    relationIds: Array<string>;
    documentIds: Array<string>;
}

export function useValidateIncidentLogStep(incidentLogId: string) {
    const { translateString } = useTranslation();
    const { selectedTags } = useTagsMultiSelectAutoUpdater(incidentLogId, TagRelationType.IncidentLog, false);
    const relationsQuery = useRelationsQuery(incidentLogId);
    const documents = useIncidentDocuments(incidentLogId);
    const { userData } = useResponsibleQuery();

    const relatedValues: IncidentLogRelatedValues = {
        selectedTags,
        relationIds: relationsQuery.data?.map((x) => x.relationId) ?? [],
        documentIds: documents.data?.map((x) => x.incidentLogDocumentId) ?? [],
    };

    function validateStep(step: EntityTemplateStep, incidentLog: IncidentLogFieldModel) {
        const result: Array<ValidationError> = [];

        step.fields.forEach((field) => {
            if (isHiddenByAnotherAnswer(field, incidentLog)) {
                return;
            }

            const fieldValidation = validateValue(getIncidentLogFieldValue(field, incidentLog, relatedValues), field, incidentLog);
            if (fieldValidation) {
                result.push(fieldValidation);
            }
        });

        return result;
    }

    function validateValue(value: any, field: EntityTemplateField, incidentLog: IncidentLogFieldModel): ValidationError | null {
        const fieldName = getIncidentLogFieldName(field);

        if (field.required && !hasFieldData(value)) {
            return new ValidationError(fieldName, translateString("requiredField"));
        }

        return fieldCustomValidation(value, fieldName, incidentLog, field);
    }

    function fieldCustomValidation(value: any, fieldName: string, incidentLog: IncidentLogFieldModel, field: EntityTemplateField): ValidationError | null {
        if (field.standardFieldIdentifier === IncidentLogStandardField.IncidentLogName && isNullOrWhitespace(value)) {
            return new ValidationError(fieldName, translateString("nameRequired"));
        }

        if (field.standardFieldIdentifier === IncidentLogStandardField.IncidentLogResponsible) {
            const user = userData?.find((x) => x.id === incidentLog.responsibleId);
            if (user && !user.active) {
                return new ValidationError(fieldName, translateString("userDeactivated"));
            }
        }

        return null;
    }

    return { validateStep, relatedValues };
}

// A field hidden by another answer cannot be filled in, so marking it required must not hold the step back.
function isHiddenByAnotherAnswer(field: EntityTemplateField, incidentLog: IncidentLogFieldModel) {
    return (
        (field.standardFieldIdentifier === IncidentLogStandardField.IncidentLogInformedOfRightToComplain &&
            !isAccessRestricted(incidentLog.accessRequestOutcome)) ||
        (field.standardFieldIdentifier === IncidentLogStandardField.IncidentLogCopyOfPersonalDataProvided &&
            !hasPersonalDataToCopy(incidentLog.accessRequestOutcome))
    );
}

export function getIncidentLogFieldName(field: EntityTemplateField) {
    return field.standardFieldIdentifier ? IncidentLogStandardField[field.standardFieldIdentifier].toString() : field.id!;
}

export function getIncidentLogFieldValue(field: EntityTemplateField, incidentLog: IncidentLogFieldModel, relatedValues: IncidentLogRelatedValues) {
    if (field.standardFieldIdentifier && isIncidentLogStandardField(field.standardFieldIdentifier)) {
        switch (field.standardFieldIdentifier) {
            case IncidentLogStandardField.IncidentLogName:
                return incidentLog.name;
            case IncidentLogStandardField.IncidentLogDescription:
                return incidentLog.description;
            case IncidentLogStandardField.IncidentLogSeverity:
                return incidentLog.severity;
            case IncidentLogStandardField.IncidentLogAccessRequestType:
                return incidentLog.accessRequestType;
            case IncidentLogStandardField.IncidentLogSuspectedUnlawfulOrMaliciousAct:
                return incidentLog.suspectedUnlawfulOrMaliciousAct;
            case IncidentLogStandardField.IncidentLogTypes:
                return incidentLog.types;
            case IncidentLogStandardField.IncidentLogBusinessAreas:
                return incidentLog.businessAreaIds;
            case IncidentLogStandardField.IncidentLogComplianceAreas:
                return incidentLog.complianceAreaIds;
            case IncidentLogStandardField.IncidentLogStatus:
                return incidentLog.status;
            case IncidentLogStandardField.IncidentLogTags:
                return relatedValues.selectedTags;
            case IncidentLogStandardField.IncidentLogResponsible:
                return incidentLog.responsibleId;
            case IncidentLogStandardField.IncidentLogNote:
                return incidentLog.note;
            case IncidentLogStandardField.IncidentLogAdditionalUserAccess:
                return incidentLog.additionalAccessUsers;
            case IncidentLogStandardField.IncidentLogExcludedUsers:
                return incidentLog.excludedUsers;
            case IncidentLogStandardField.IncidentLogVisibleToAllUsers:
                return incidentLog.visibleToAllUsers;
            case IncidentLogStandardField.IncidentLogBusinessAreaAccess:
                return incidentLog.accessBusinessAreas;
            case IncidentLogStandardField.IncidentLogGroupCompanies:
                return incidentLog.allGroupEntityAccess || incidentLog.groupEntities.length > 0;
            case IncidentLogStandardField.IncidentLogDiscoveryDate:
                return incidentLog.discoveryDate;
            case IncidentLogStandardField.IncidentLogReceiptDate:
                return incidentLog.receiptDate;
            case IncidentLogStandardField.IncidentLogSignificantIncidentAwarenessDate:
                return incidentLog.significantIncidentAwarenessDate;
            case IncidentLogStandardField.IncidentLogDeadlineExtended:
                return incidentLog.deadlineExtended;
            case IncidentLogStandardField.IncidentLogDataSubjectName:
                return incidentLog.dataSubjectName;
            case IncidentLogStandardField.IncidentLogIdentityVerified:
                return incidentLog.identityVerified;
            case IncidentLogStandardField.IncidentLogRequestMadeThroughRepresentative:
                return incidentLog.requestMadeThroughRepresentative;
            case IncidentLogStandardField.IncidentLogRequestedDataCategories:
                return incidentLog.requestedDataCategoryIds;
            case IncidentLogStandardField.IncidentLogAccessRequestOutcome:
                return incidentLog.accessRequestOutcome;
            case IncidentLogStandardField.IncidentLogResponseSent:
                return incidentLog.responseSent;
            case IncidentLogStandardField.IncidentLogCopyOfPersonalDataProvided:
                return incidentLog.copyOfPersonalDataProvided;
            case IncidentLogStandardField.IncidentLogInformedOfRightToComplain:
                return incidentLog.informedOfRightToComplain;
            case IncidentLogStandardField.IncidentLogStartDate:
                return incidentLog.isStartUnknown || hasFieldData(incidentLog.startDate);
            case IncidentLogStandardField.IncidentLogEndDate:
                return incidentLog.isEndDateOngoing || hasFieldData(incidentLog.endDate);
            case IncidentLogStandardField.IncidentLogRecoveryDate:
                return incidentLog.recoveryDate;
            case IncidentLogStandardField.IncidentLogSupplierNotificationDate:
                return incidentLog.supplierNotificationDate;
            case IncidentLogStandardField.IncidentLogDataCategories:
                return incidentLog.dataCategoryIds;
            case IncidentLogStandardField.IncidentLogDataSubjects:
                return incidentLog.dataSubjectIds;
            case IncidentLogStandardField.IncidentLogEstimatedNumberOfAffectedPeople:
                return incidentLog.isEstimatedNumberOfAffectedPeopleUnknown || hasFieldData(incidentLog.estimatedNumberOfAffectedPeople);
            case IncidentLogStandardField.IncidentLogEstimatedNumberOfAffectedRecords:
                return incidentLog.isEstimatedNumberOfAffectedRecordsUnknown || hasFieldData(incidentLog.estimatedNumberOfAffectedRecords);
            case IncidentLogStandardField.IncidentLogAffectedServices:
                return incidentLog.affectedServiceIds;
            case IncidentLogStandardField.IncidentLogAffectedSecurityProperties:
                return incidentLog.affectedSecurityProperties;
            case IncidentLogStandardField.IncidentLogPersonalDataInvolved:
                return incidentLog.personalDataInvolved;
            case IncidentLogStandardField.IncidentLogEstimatedNumberOfAffectedServiceRecipients:
                return incidentLog.estimatedNumberOfAffectedServiceRecipients;
            // The currency always has a value to show, so only the amount says whether the loss has been filled in.
            case IncidentLogStandardField.IncidentLogEstimatedFinancialLoss:
                return incidentLog.estimatedFinancialLoss;
            // The affected countries only follow a yes, so the answer alone says whether the field has been filled in.
            case IncidentLogStandardField.IncidentLogCrossBorderImpact:
                return incidentLog.crossBorderImpact;
            case IncidentLogStandardField.IncidentLogRelations:
                return relatedValues.relationIds;
            case IncidentLogStandardField.IncidentLogConsequences:
                return incidentLog.consequences;
            case IncidentLogStandardField.IncidentLogMeasures:
                return incidentLog.measures;
            case IncidentLogStandardField.IncidentLogReportedToAgency:
                return incidentLog.reportedToAgency;
            case IncidentLogStandardField.IncidentLogAffectedPeopleNotified:
                return incidentLog.affectedPeopleNotified;
            case IncidentLogStandardField.IncidentLogReportedToAuthorities:
                return incidentLog.reportedToAuthorities;
            case IncidentLogStandardField.IncidentLogServiceRecipientsNotified:
                return incidentLog.serviceRecipientsNotified;
            case IncidentLogStandardField.IncidentLogInformationSecurityReportedToAuthorities:
                return incidentLog.informationSecurityReportedToAuthorities;
            case IncidentLogStandardField.IncidentLogAffectedPartiesNotified:
                return incidentLog.affectedPartiesNotified;
            case IncidentLogStandardField.IncidentLogDocuments:
                return relatedValues.documentIds;
            default:
                return exhaustiveGuard(field.standardFieldIdentifier);
        }
    }

    return incidentLog.customFieldValues?.find((x) => x.customInputFieldId === field.customInputFieldId)?.value;
}
